
"use strict";
import UrlService from './urlService';
import mktRegionMapping from './regionMapping.js';
import axios from 'axios';
import { getConfiguration, getCachedConfigField } from '@nuskin/configuration-sdk';

/**
 *
 * TdcStrings
 *
 * @namespace TdcStrings
 */

let neededStrings = {
    cart: {
        adrContinueTxt: '',
        adrShipDay: '',
        minPsvErrorTxt: '',
        minPsvWarningTxt: '',
        numAdrsTxt: '',
        qtyChangedAtpErrorTxt: '',
        replaceOnAdrListHeader: '',
        scanCardLimitTxt: '',
        searchAtpErrorTxt: '',
        exclusiveProductLearnMoreURL: ''
    },
    checkout: {
        contractLengthTextSmall: '',
        firstOrderText: '',
        firstShipTextSmall: '',
        recurringOrderTextFuture: '',
        recurringOrderTextImmediate: '',
        recurringShipDayTextSmall: '',
        replacement: '',
        selectContractLengthText: '',
        agelocLoyaltyContractText: ''
    }
};

/**
 * Retrieves AEM TDC properties for the page at the given path. Only properties
 * prefixed with 'tdc_' are retrieved, but are returned with this prefix removed.
 *
 * If the requested page cannot be found, an empty object will be returned.
 *
 * The page path can be relative (with or without a leading slash) or absolute.
 *
 * Examples:
 * * __Relative Path, Leading Slash:__ calling `getTdcStringsAsync('/my/page')`
 * from https://test.nuskin.com/other/page will retrieve properties for the
 * page at https://test.nuskin.com/my/page.
 * * __Relative Path, No Leading Slash:__ calling
 * `getTdcStringsAsync('my/page')` from https://dev.nuskin.com/other/page will
 * retrieve properties for the page at https://dev.nuskin.com/other/my/page.
 * * __Absolute Path:__ calling `getTdcStringsAsync('https://www.nuskin.com/my/page')`
 * from https://test.nuskin.com/other/page will retrieve properties for the
 * page at https://www.nuskin.com/my/page.
 * @param {string} path URL path
 */
async function getTdcStringsAsync(path) {
    return getServiceJSON(path, 'noTdc_');
}

/**
 * Retrieves AEM properties for the page at the given path. Only non-TDC
 * properties (i.e. not prefixed with 'tdc_') are retrieved.
 *
 * If the requested page cannot be found, an empty object will be returned.
 *
 * The page path can be relative (with or without a leading slash) or absolute.
 *
 * Examples:
 * * __Relative Path, Leading Slash:__ calling `getNonTdcStringsAsync('/my/page')`
 * from https://test.nuskin.com/other/page will retrieve properties for the
 * page at https://test.nuskin.com/my/page.
 * * __Relative Path, No Leading Slash:__ calling
 * `getNonTdcStringsAsync('my/page')` from https://dev.nuskin.com/other/page
 * will retrieve properties for the page at https://dev.nuskin.com/other/my/page.
 * * __Absolute Path:__ calling `getNonTdcStringsAsync('https://www.nuskin.com/my/page')`
 * from https://test.nuskin.com/other/page will retrieve properties for the
 * page at https://www.nuskin.com/my/page.
 * @param {string} path URL path
 */
async function getNonTdcStringsAsync(path) {
    return getServiceJSON(path, 'nonTdc');
}

/**
 * @param {string} path
 * @param {string} selector
 */
async function getServiceJSON(path, selector) {
    const suffix = `.service.${selector}.json`;
    const url = new URL(path, window.location.href).href.replace(/\.\w+$/, '') + suffix;

    try {
        const { data } = await axios.get(url, {
            headers: { Accept: 'application/json' }
        });
        return data;
    } catch (e) {
        console.error(e);
        return {};
    }
}

let getTdcStrings = async function(page, language, country, angApp) {
    return doStringsRequest(page, language, country, angApp, 'noTdc_');
};

let getNonTdcStrings = async function(page, language, country, angApp) {
    return doStringsRequest(page, language, country, angApp, 'nonTdc');
};

async function doStringsRequest(page, language, country, angApp, mode) {
    let tdc = {};
    let url;

    if (UrlService.isMeCommerce()){
        url = window.location.protocol + "//" + window.location.host + '/content/mecommerce/{region}/{market}/{language}/shop/{page}.service.' + mode + '.json';
        url = url.replace('{region}', mktRegionMapping[country]).replace('{market}', country).replace('{language}', language).replace('{page}', page);
    } else {
        const {Url: urlCfg} = await getConfiguration(['Url', 'Basic']);
        url = UrlService.extractHostURL(urlCfg.tdcStringsUrl);
        if (typeof url !== "undefined") {
            url = url.replace('{language}', language).replace('{market}', country).replace('{page}', page).replace('.noTdc_.', '.' + mode + '.');
        }
    }
    $.ajax({
        type: "GET",
        url: url,
        dataType: "json",
        async:false
    }).done((data) => {
        tdc = addNeededStrings(data, neededStrings[page]);
        tdc = transformTdcStrings(tdc);
    }).fail((xhr) => {
        console.log("could not load tdc for the Checkout! error:"+xhr);
    });

    if (angApp) {
        angApp.constant("TDC", tdc);
    }

    return tdc;
}

function transformTdcStrings(tdc) {
    let adrProgram = getCachedConfigField('adrProgram') || 'ADR',
        tdcString;

    for (tdcString in tdc) {
        if (tdc.hasOwnProperty(tdcString)) {
            tdc[tdcString] = replaceAdrProgram(tdc[tdcString], adrProgram);
            tdc[tdcString] = escapeIllegalLinks(tdc[tdcString]);
            //            str = StringEscapeUtils.escapeHtml4(str);


            if (tdcString === 'processMyAdrText') {
                tdc[tdcString] = tdc[tdcString].replace('<p>', '').replace('</p>', '');
            }
        }

    }

    return tdc;
}

function addNeededStrings(tdc, requiredStrings) {
    if (requiredStrings) {
        for (let neededString in requiredStrings) {
            if (requiredStrings.hasOwnProperty(neededString)) {
                if (!tdc[neededString]) {
                    tdc[neededString] = requiredStrings[neededString];
                }
            }
        }
    }

    return tdc;
}

function escapeIllegalLinks(str) {
    let retStr = str ? str : "";

    if (typeof retStr === 'string') {
        retStr = retStr.replace("author.nuskin","www.nuskin");
    }

    return retStr;
}

function replaceAdrProgram(str, adrProgram) {
    let retStr = str ? str : "";
    if (typeof retStr === 'string') {
        retStr = retStr.replace("ADR", adrProgram);
    }
    return retStr;
}

export default {
    getTdcStringsAsync,
    getNonTdcStringsAsync,
    getStrings: getTdcStrings,
    getNonTdcStrings: getNonTdcStrings
}
